import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { BadgeCheckIcon, BriefcaseIcon, Building2Icon, CalendarIcon, HashIcon, LayersIcon, MailIcon, MapPinIcon, NetworkIcon, PhoneIcon, SmartphoneIcon, UsersIcon } from 'lucide-react';
import type { Employee, Navigate } from '../../types/hr';
import type { IconComponent } from '../../types/ui';
import { directReports, displayName, getEmployee } from '../../utils/employees';
import { formatDate, tenure } from '../../utils/format';
import { Avatar } from '../ui/Avatar';
import { OrgChartModal } from './OrgChartModal';
interface VitalsCardProps {
  employee: Employee;
  onNavigate: Navigate;
}
export function VitalsCard({
  employee,
  onNavigate
}: VitalsCardProps) {
  const [orgOpen, setOrgOpen] = useState(false);
  const manager = getEmployee(employee.managerId);
  const reports = directReports(employee.id);
  return <aside className="rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-538" data-mp="k5ghgkj9qh3e">
      <div className="px-5 pb-2 pt-5" data-id="element-539" data-mp="26ue58fr929o7">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-540" data-mp="26vfdkcgvsi28">Vitals</h2>
      </div>

      <ul className="space-y-0.5 px-2" data-id="element-541" data-mp="22u3vqt49114l">
        <Row icon={PhoneIcon} label="Work phone" data-id="element-542" data-mp="2gsk4eplo91pd">
          {employee.workPhone} <span className="text-muted" data-id="element-543" data-mp="2gtv7en7am5ff">x{employee.workExt}</span>
        </Row>
        <Row icon={SmartphoneIcon} label="Mobile" data-id="element-544" data-mp="2bspeao9q0g5d">
          {employee.mobile}
        </Row>
        <Row icon={MailIcon} label="Work email" data-id="element-545" data-mp="2ps6x8n0c4z6t">
          <a href={`mailto:${employee.workEmail}`} className="break-all text-azure hover:underline" data-id="element-546" data-mp="2pqvsokz2fnen">
            {employee.workEmail}
          </a>
        </Row>
        <Row icon={MapPinIcon} label="Location" data-id="element-547" data-mp="2zhqi1y7lz34b">
          {employee.location}
        </Row>
      </ul>

      <div className="mx-5 my-3 h-px bg-slate-100" data-id="element-548" data-mp="2uki5cd58brag" />

      <ul className="space-y-0.5 px-2" data-id="element-549" data-mp="39gzeqytuyvud">
        <Row icon={BriefcaseIcon} label="Job title" data-id="element-550" data-mp="39hr1dk5h9j8i">
          {employee.jobTitle}
        </Row>
        <Row icon={BadgeCheckIcon} label="Employment type" data-id="element-551" data-mp="34jfuniahboui">
          {employee.employmentType}
        </Row>
        <Row icon={Building2Icon} label="Department" data-id="element-552" data-mp="3bn5ttkfjynfb">
          {employee.department}
        </Row>
        <Row icon={LayersIcon} label="Division" data-id="element-553" data-mp="3gkp7ai4few67">
          {employee.division}
        </Row>
        <Row icon={HashIcon} label="Employee #" data-id="element-554" data-mp="3lhxsysos9ic7">
          {employee.employeeNumber}
        </Row>
        <Row icon={CalendarIcon} label="Hire date" data-id="element-555" data-mp="3qbtjja2pw2xc">
          {formatDate(employee.hireDate)} <span className="text-muted" data-id="element-556" data-mp="3v8jzzyifp0ja">· {tenure(employee.hireDate)}</span>
        </Row>
      </ul>

      <div className="mx-5 my-3 h-px bg-slate-100" data-id="element-557" data-mp="3481bm1ilbx1" />

      <div className="px-5" data-id="element-558" data-mp="84m8l1vakhu0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-559" data-mp="84cjrhlzggpw">Manager</p>
        {manager ? <button type="button" onClick={() => onNavigate({
        page: 'profile',
        employeeId: manager.id
      })} className="-mx-2 mt-2 flex w-[calc(100%+16px)] items-center gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-canvas" data-id="element-560" data-mp="d2e3m4r8gk2f">
            <Avatar name={displayName(manager)} seed={manager.id} size="sm" data-id="element-561" data-mp="27bsx2jrgl6sm" />
            <span className="min-w-0" data-id="element-562" data-mp="22l48bc75ynx6">
              <span className="block truncate text-sm font-medium text-navy" data-id="element-563" data-mp="22k2l69cviaz3">{displayName(manager)}</span>
              <span className="block truncate text-xs text-muted" data-id="element-564" data-mp="1xm9elf7nreya">{manager.jobTitle}</span>
            </span>
          </button> : <p className="mt-2 text-sm text-slate-400" data-id="element-565" data-mp="2qaezc1ypp3y4">No manager — top of org</p>}
      </div>

      <div className="mt-3 px-5" data-id="element-566" data-mp="2lbc57ck9si4x">
        <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-567" data-mp="2lcdilnordgpt">
          <UsersIcon className="h-3 w-3" data-id="element-568" data-mp="2gaxvzr6xn25b" /> Direct reports {reports.length > 0 && <span className="text-navy" data-id="element-569" data-mp="2bdo0btsy9u91">· {reports.length}</span>}
        </p>
        {reports.length === 0 ? <p className="mt-2 text-sm text-slate-400" data-id="element-570" data-mp="14xc7874zj2kb">None</p> : <ul className="-mx-2 mt-1.5" data-id="element-571" data-mp="zy92yowtx0de">
            {reports.slice(0, 5).map(r => <li key={r.id} data-id="element-572" data-mp="2os6n8pebuskr">
                <button type="button" onClick={() => onNavigate({
            page: 'profile',
            employeeId: r.id
          })} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition hover:bg-canvas" data-id="element-573" data-mp="2orwu4sxzk5au">
                  <Avatar name={displayName(r)} seed={r.id} size="xs" data-id="element-574" data-mp="2jstyu07t5dg2" />
                  <span className="truncate text-sm text-slate-700" data-id="element-575" data-mp="2jqr26xc5gm2d">{displayName(r)}</span>
                </button>
              </li>)}
            {reports.length > 5 && <li data-id="element-576" data-mp="3cgzkcp6or6gy">
                <button type="button" onClick={() => setOrgOpen(true)} className="px-2 pt-1 text-xs font-medium text-azure hover:underline" data-id="element-577" data-mp="3cfepszkdfnj3">
                  +{reports.length - 5} more
                </button>
              </li>}
          </ul>}
      </div>

      <div className="mt-4 border-t border-slate-100 px-5 py-4" data-id="element-578" data-mp="1m40fa5maa3bg">
        <button type="button" onClick={() => setOrgOpen(true)} className="inline-flex items-center gap-2 text-sm font-medium text-azure hover:underline" data-id="element-579" data-mp="1r5hd1s9h19nt">
          <NetworkIcon className="h-4 w-4" data-id="element-580" data-mp="47oy6nzcztes" />
          View in org chart
        </button>
      </div>

      <OrgChartModal open={orgOpen} employee={employee} onClose={() => setOrgOpen(false)} onNavigate={onNavigate} data-id="element-581" data-mp="i7hk85zxxlgf" />
    </aside>;
}
function Row({
  icon: Icon,
  label,
  children
}: {
  icon: IconComponent;
  label: string;
  children: ReactNode;
}) {
  return <li className="flex items-start gap-3 rounded-lg px-3 py-2" data-id="element-582" data-mp="3mmhavlx9r81y">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-mutedlight" aria-hidden="true" data-id="element-583" data-mp="3hn4r5a1p2h3l" />
      <div className="min-w-0" data-id="element-584" data-mp="3vdrfc89twolf">
        <p className="text-[11px] text-muted" data-id="element-585" data-mp="3vf1ez1wby4u6">{label}</p>
        <p className="text-sm text-slate-800" data-id="element-586" data-mp="3qnb1in23j0yj">{children}</p>
      </div>
    </li>;
}